/**
 * The campaign engine, ported for the fixture provider.
 *
 * The console does not get to invent what a campaign looks like: `GET
 * /ops/v1/campaigns/{id}` answers numbers that are DERIVED at read time, and the
 * derivation lives in `acs_service/campaigns.py` (`wave_slice`, `plan`,
 * `open_wave_jobs`) and `acs_service/routers/firmware.py` (`_wave_counts`,
 * `_compute_targets`, `_progress`, `_campaign_item`, `_campaign_detail`). This file
 * mirrors those functions — same order of checks, same reason strings, same
 * rounding — so a page built on fixture data is built against the ladder the façade
 * actually runs. Each block cites the Python lines it ports.
 *
 * What is deliberately NOT ported here is the dispatch loop (`dispatch_wave`): it
 * belongs to E8-b, because a read that moves the fleet is exactly the surprise the
 * maintenance window exists to prevent.
 */

import type {
  Campaign,
  CampaignAction,
  CampaignFilter,
  CampaignKind,
  CampaignListItem,
  CampaignNextAction,
  CampaignProgress,
  CampaignWave,
  CampaignWindow,
} from '../api/types';

/** `campaigns.py:55-58` — the `campaign.progress` phases. */
export const PHASE_STARTED = 'started';
export const PHASE_WAVE_DONE = 'wave_done';
export const PHASE_HALTED = 'halted';
export const PHASE_COMPLETED = 'completed';

/** `job_machine.py:70` — the states that close a job. */
export const TERMINAL_JOB_STATUSES: readonly string[] = [
  'completed',
  'failed',
  'timeout',
  'cancelled',
];

/** `campaigns.py:251` — statuses with nothing left for anyone to dispatch. */
export const SETTLED_STATUSES: readonly string[] = ['completed', 'halted', 'cancelled', 'failed'];

/** `campaigns.py:254` — the ladder only ever moves forward for a rollout. */
export const LADDER_KIND: CampaignKind = 'rollout';

/** `windows.py:29-32` — the shape quoted in every refusal, and the interval separator. */
export const WINDOW_SHAPE =
  'an ISO-8601 UTC interval `YYYY-MM-DDTHH:MM:SSZ/YYYY-MM-DDTHH:MM:SSZ`, ' +
  'e.g. 2026-10-14T02:00:00Z/2026-10-14T04:00:00Z';
export const WINDOW_SEPARATOR = '/';

/** One entry of the campaign's internal job map (never exposed on the wire). */
export interface CampaignJobEntry {
  job_id: string;
  device_id: string;
  kind: string;
  wave: number;
}

/** The campaign record as the store holds it — the shape the router reads. */
export interface CampaignDoc {
  campaign_id: string;
  status: string;
  file_id: string;
  brand: string;
  model: string;
  version: string;
  filter: CampaignFilter;
  waves: CampaignWave[];
  per_wave: number[];
  current_wave: number;
  targets: string[];
  queued: string[];
  jobs: CampaignJobEntry[];
  halt_on_fail_pct: number;
  maintenance_window: string | null;
  dispatch_kind: string;
  dispatch_file_id: string;
  started_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** U-04 stamp: operator-profile version active at mint; absent/null = predates stamp. */
  profile_version?: number | null;
}

/** The two facts `_progress` and `open_wave_jobs` read off a job record. */
export interface CampaignJobView {
  status: string;
  verdict?: string;
}

/** The mirror of `store.get_job` the router calls for every job entry. */
export interface CampaignJobReader {
  getJob(jobId: string): CampaignJobView | undefined;
}

/** A device row the population scan sees — the fields `_compute_targets` reads. */
export interface CampaignPopulationDevice {
  acs_device_id: string;
  manufacturer: string;
  product_class: string;
  software_version?: string | null;
  tags?: string[] | null;
}

/* ---------------------------------------------------------------- the wave maths --- */

/**
 * `firmware.py:403-421` — the ladder is CUMULATIVE coverage: strictly increasing and
 * ending at 100, each rung between 1 and 100.
 *
 * P7's own canary ladder `5 → 25 → 100` sums to 130, which is why the check is ordering
 * plus final coverage, not a literal sum. The refusal detail is the server's, including
 * the percents it was given, because the operator's next move is re-typing the ladder.
 */
export function validateWaves(pcts: readonly number[]): number[] {
  const bad =
    pcts.length === 0 ||
    pcts.some((pct) => pct < 1 || pct > 100) ||
    pcts.some((pct, index) => index > 0 && pct - (pcts[index - 1] as number) <= 0) ||
    pcts[pcts.length - 1] !== 100;
  if (bad) {
    throw new Error(
      'waves must be strictly increasing cumulative percentages between 1 and 100 ending at 100 ' +
        `(got [${pcts.join(', ')}])`,
    );
  }
  return [...pcts];
}

/** `campaigns.py:117-119` — devices belonging to one rung. */
export function waveTotal(doc: CampaignDoc, wave: number): number {
  const perWave = doc.per_wave ?? [];
  const value = wave >= 0 && wave < perWave.length ? perWave[wave] : undefined;
  return value ?? 0;
}

/** `campaigns.py:122-127` — a rollout wave slice, or the whole population for a rollback. */
export function passTotal(doc: CampaignDoc, wave: number, kind: string): number {
  if (kind === 'rollback') return (doc.targets ?? []).length;
  return waveTotal(doc, wave);
}

/** `campaigns.py:295-302` — the devices of one wave, in ladder order. */
export function waveSlice(doc: CampaignDoc, wave: number): string[] {
  const targets = doc.targets ?? [];
  const perWave = doc.per_wave ?? [];
  const size = wave >= 0 && wave < perWave.length ? perWave[wave] : undefined;
  if (size === undefined) return [];
  const start = perWave.slice(0, wave).reduce((a, b) => a + b, 0);
  return targets.slice(start, start + size);
}

/**
 * `firmware.py:421-441` — devices per wave for a population of `total`.
 *
 * Cumulative bounds use `ceil` so the canary of a small fleet is at least one device
 * (5% of 10 = 0.5 → 1, not 0) and the last wave absorbs the rounding remainder: a
 * ladder can never silently skip devices.
 */
export function waveCounts(total: number, pcts: readonly number[]): number[] {
  const bounds: number[] = [];
  let previous = 0;
  pcts.forEach((pct, index) => {
    const raw = index === pcts.length - 1 ? total : Math.min(total, Math.ceil((total * pct) / 100));
    const end = Math.max(previous, raw);
    bounds.push(end);
    previous = end;
  });
  const counts: number[] = [];
  let cursor = 0;
  for (const end of bounds) {
    counts.push(end - cursor);
    cursor = end;
  }
  return counts;
}

/** `campaigns.py:130-152` — `(confirmed, not confirmed)` for one wave, read off the verdicts. */
export function waveJobCounts(
  store: CampaignJobReader,
  doc: CampaignDoc,
  wave: number,
  kind: string,
): { ok: number; failed: number } {
  let ok = 0;
  let failed = 0;
  for (const entry of doc.jobs ?? []) {
    if ((entry.wave ?? 0) !== wave || entry.kind !== kind) continue;
    const job = store.getJob(entry.job_id);
    const status = job?.status ?? '';
    if (!TERMINAL_JOB_STATUSES.includes(status)) continue;
    if (job?.verdict === 'applied') ok += 1;
    else failed += 1;
  }
  return { ok, failed };
}

/** `campaigns.py:155-164` — jobs of one wave the verify pass has not concluded. */
export function openWaveJobs(
  store: CampaignJobReader,
  doc: CampaignDoc,
  wave: number,
  kind: string,
): number {
  let pending = 0;
  for (const entry of doc.jobs ?? []) {
    if ((entry.wave ?? 0) !== wave || entry.kind !== kind) continue;
    const status = store.getJob(entry.job_id)?.status ?? '';
    if (!TERMINAL_JOB_STATUSES.includes(status)) pending += 1;
  }
  return pending;
}

/** `campaigns.py:167-179` — P7 auto-halt, in percent over the devices that actually ran. */
export function haltTriggered(doc: CampaignDoc, ok: number, failed: number): boolean {
  const attempted = ok + failed;
  if (!attempted) return false;
  const threshold = doc.halt_on_fail_pct ?? 10;
  return failed * 100 > threshold * attempted;
}

/** `campaigns.py:182-187` — the record's state once a wave's jobs have all concluded. */
export function terminalFor(doc: CampaignDoc, wave: number): { status: string; phase: string } {
  const waves = doc.waves ?? [];
  if (wave >= waves.length - 1) return { status: 'completed', phase: PHASE_COMPLETED };
  return { status: 'running', phase: PHASE_WAVE_DONE };
}

/* ------------------------------------------------------------------- the window --- */

/** `windows.py:52-68` — `(start, end)`, or null when nothing was declared. */
export function parseWindow(text: string | null | undefined): [Date, Date] | null {
  const trimmed = (text ?? '').trim();
  if (!trimmed) return null;
  const parts = trimmed.split(WINDOW_SEPARATOR);
  if (parts.length !== 2) rejectWindow('a maintenance window is exactly one interval');
  const start = instant(parts[0] as string, 'start');
  const end = instant(parts[1] as string, 'end');
  if (start >= end) {
    rejectWindow(
      `window start ${isoUtc(start)} must precede its end ${isoUtc(end)} ` +
        '— an empty interval would strand the campaign',
    );
  }
  return [start, end];
}

function rejectWindow(why: string): never {
  throw new Error(`${why} — expected ${WINDOW_SHAPE}`);
}

/**
 * `windows.py:40-49` — an instant with no zone is refused, because it is a window two
 * operators read differently and the difference lands on subscribers at 02:00.
 */
function instant(text: string, side: 'start' | 'end'): Date {
  const value = text.trim();
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    rejectWindow(`${side} endpoint ${JSON.stringify(value)} is not an ISO-8601 timestamp`);
  }
  // `fromisoformat` refuses a naive instant; `Date` accepts one, so the zone has to be
  // read off the text itself — `Z` or an explicit `±HH:MM` offset.
  if (!/(Z|[+-]\d{2}:?\d{2})$/.test(value)) {
    rejectWindow(
      `${side} endpoint ${JSON.stringify(value)} has no UTC offset — ` +
        'a window without a zone is a window two operators read differently',
    );
  }
  return parsed;
}

/** `windows.py:71-93` — what the window means right now, as data an operator can be handed. */
export function windowState(text: string | null | undefined, now: Date): CampaignWindow | null {
  const window = parseWindow(text);
  if (window === null) return null;
  const [start, end] = window;
  let status: CampaignWindow['status'];
  let open: boolean;
  if (now < start) {
    status = 'waiting';
    open = false;
  } else if (now < end) {
    status = 'open';
    open = true;
  } else {
    status = 'passed';
    open = false;
  }
  return { status, open, starts_at: isoUtc(start), ends_at: isoUtc(end) };
}

/** Python's `datetime.isoformat().replace("+00:00", "Z")` for a UTC instant. */
function isoUtc(value: Date): string {
  return `${value.toISOString().slice(0, 19)}Z`;
}

/* ------------------------------------------------------------------ the decision --- */

/**
 * `campaigns.py:407-459` — what this campaign should do right now.
 *
 * The order of the checks is the contract: a closed window is read BEFORE the queue,
 * a queued wave before the ladder, and a wave with unconfirmed jobs never advances.
 * The reason strings are the operator's sentence, copied from the server so the same
 * question gets the same words on both sides of the wire.
 */
export function planFor(store: CampaignJobReader, doc: CampaignDoc, now: Date): CampaignNextAction {
  const status = doc.status ?? '';
  const wave = doc.current_wave ?? 0;
  const kind = doc.dispatch_kind || LADDER_KIND;
  const queued = [...(doc.queued ?? [])];
  const window = windowState(doc.maintenance_window, now);
  const base: Omit<CampaignNextAction, 'action' | 'reason'> = {
    campaign_id: doc.campaign_id ?? '',
    wave,
    kind: kind as CampaignKind,
    status,
    window,
  };
  const decided = (
    action: CampaignAction,
    reason: string,
    extra: Partial<CampaignNextAction> = {},
  ) => ({ ...base, action, reason, ...extra }) as CampaignNextAction;

  if (SETTLED_STATUSES.includes(status)) {
    return decided('settled', `campaign is ${status}; nothing is dispatched`);
  }
  if (window !== null && !window.open) {
    return decided(
      'wait_window',
      `maintenance window is ${window.status} (opens ${window.starts_at}, closes ${window.ends_at})`,
    );
  }
  if (queued.length > 0) {
    return decided(
      'continue_wave',
      `wave ${wave} still has ${queued.length} device(s) undispatched from a rate-limited pass`,
      { devices: queued },
    );
  }
  if (kind !== LADDER_KIND) {
    return decided(
      'idle',
      `a ${kind} pass is one pass over the population; the ladder only advances for a rollout`,
    );
  }
  const open = openWaveJobs(store, doc, wave, kind);
  if (open > 0) {
    return decided(
      'wait_jobs',
      `wave ${wave} has ${open} job(s) the verify pass has not confirmed off the board yet`,
      {
        open_jobs: open,
      },
    );
  }
  const waves = doc.waves ?? [];
  if (wave + 1 >= waves.length) {
    return decided('idle', `wave ${wave} is the last of the ladder`);
  }
  const devices = waveSlice(doc, wave + 1);
  if (devices.length === 0) {
    return decided('idle', `wave ${wave + 1} has no device in this population`);
  }
  return decided(
    'advance',
    `wave ${wave} concluded; wave ${wave + 1} (${devices.length} device(s)) is next`,
    {
      wave: wave + 1,
      devices,
    },
  );
}

/* ------------------------------------------------------------- the read-time views --- */

/** `firmware.py:565-573` — job-derived progress; no worker ever writes these numbers. */
export function progressOf(store: CampaignJobReader, doc: CampaignDoc): CampaignProgress {
  const counts: CampaignProgress = { queued: 0, running: 0, completed: 0, failed: 0, total: 0 };
  for (const entry of doc.jobs ?? []) {
    const status = store.getJob(entry.job_id)?.status ?? 'queued';
    if (
      status === 'queued' ||
      status === 'running' ||
      status === 'completed' ||
      status === 'failed'
    ) {
      counts[status] += 1;
    } else {
      counts.queued += 1;
    }
    counts.total += 1;
  }
  return counts;
}

/**
 * `firmware.py:576-599` — the list view.
 *
 * `queued_devices` is what a rate-limited dispatch left of the CURRENT wave (what
 * `:resume` continues); `remaining_devices` is the part of the population no wave has
 * touched yet. Two different backlogs, deliberately two numbers.
 */
export function campaignItem(store: CampaignJobReader, doc: CampaignDoc): CampaignListItem {
  const targets = doc.targets ?? [];
  const dispatched = new Set(
    (doc.jobs ?? []).filter((entry) => entry.kind === 'rollout').map((entry) => entry.device_id),
  );
  return {
    campaign_id: doc.campaign_id ?? '',
    status: doc.status as CampaignListItem['status'],
    file_id: doc.file_id ?? '',
    brand: doc.brand ?? '',
    model: doc.model ?? '',
    version: doc.version ?? '',
    current_wave: doc.current_wave ?? 0,
    per_wave: [...(doc.per_wave ?? [])],
    population: targets.length,
    queued_devices: (doc.queued ?? []).length,
    remaining_devices: Math.max(0, targets.length - dispatched.size),
    progress: progressOf(store, doc),
    started_at: doc.started_at ?? null,
    created_at: doc.created_at ?? null,
    profile_version: doc.profile_version ?? null,
  };
}

/**
 * `firmware.py:602-619` — the detail adds the operator's inputs, the population and
 * `next_action`, read at the same instant the caller asks with (the router uses
 * `datetime.now(timezone.utc)`; a test passes its own clock).
 */
export function campaignDetail(
  store: CampaignJobReader,
  doc: CampaignDoc,
  now: Date = new Date(),
): Campaign {
  return {
    ...campaignItem(store, doc),
    filter: doc.filter ?? {},
    waves: [...(doc.waves ?? [])],
    targets: [...(doc.targets ?? [])],
    queued: [...(doc.queued ?? [])],
    halt_on_fail_pct: doc.halt_on_fail_pct ?? 10,
    maintenance_window: doc.maintenance_window ?? null,
    next_action: planFor(store, doc, now),
    dispatch_kind: doc.dispatch_kind as Campaign['dispatch_kind'],
    dispatch_file_id: doc.dispatch_file_id ?? '',
    updated_at: doc.updated_at ?? null,
  };
}

/**
 * `firmware.py:489-520` — the campaign population, in deterministic wave order.
 *
 * Tags are AND-ed because a firmware push is destructive: a multi-tag filter narrows
 * the population instead of unioning groups nobody meant to flash together.
 * `from_version` requires the device to be verifiably running it (an unknown version
 * never matches), and a device already running the campaign image is skipped.
 *
 * `currentVersion` is injected because the server reads it from the parameter
 * snapshot first and the device doc second (`campaigns.py:277-293`); the fixture
 * passes the same order over its own snapshot.
 */
export function computeTargets(
  devices: readonly CampaignPopulationDevice[],
  image: { version?: string },
  filter: CampaignFilter,
  currentVersion: (device: CampaignPopulationDevice) => string | null,
): string[] {
  const wantedTags = new Set((filter.tags ?? []).filter(Boolean));
  const imageVersion = image.version ?? '';
  const targets: string[] = [];
  for (const device of devices) {
    const deviceId = device.acs_device_id ?? '';
    if (!deviceId) continue;
    if (filter.manufacturer && device.manufacturer !== filter.manufacturer) continue;
    // the ACS calls the model ProductClass (`query_devices({"product_class": …})`)
    if (filter.model && device.product_class !== filter.model) continue;
    if (wantedTags.size > 0) {
      const tags = new Set(device.tags ?? []);
      const carries = [...wantedTags].every((tag) => tags.has(tag));
      if (!carries) continue;
    }
    const current = currentVersion(device);
    if (filter.from_version && current !== filter.from_version) continue;
    if (imageVersion && current === imageVersion) continue;
    targets.push(deviceId);
  }
  return targets;
}
